import { useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "app/hooks";
import { AbilityContext } from "context/Ability";
import { useNavigate, useSearchParams } from "react-router-dom";

// Material UI
import { Typography } from "@mui/material";

// Components
import Loading from "components/Layout/Loading";

// Hooks
import useAuth from "utils/hooks/useAuth";
import { useAbility } from "@casl/react";

// Constants
import { REDIRECT_PACE, REDIRECT_LD } from "constants/constants";

// Enums
import { urlPaths } from "enums/urlPaths";

// Types
import { RequestInfo } from "store/slice/store.types";
import { updateLandingPageService, updateUserOrg } from "services";
import { fetchUserOrgsThunk, getUserOrgs } from "store/slice/sysadmin/userOrgs/userOrgsSlice";
import { fetchSession } from "store/slice/auth";
import qs from "qs";

const Home = (): JSX.Element => {
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const userOrgs = useAppSelector(getUserOrgs);

    const ability = useAbility(AbilityContext);
    const { sessionData, hasSession, fetched, error } = useAuth();

    const [searchParams] = useSearchParams();
    const landingPageParam = searchParams.get("landing_page");
    const defaultOrg = searchParams.get("default_org");

    const [avoidMultipleGetSession, setAvoidMultipleGetSession] = useState(false);
    const [orgUpdated, setOrgUpdated] = useState(false);
    const [requestStatus, setRequestStatus] = useState<RequestInfo>("pristine");
    const lastPathname = localStorage.getItem("lastPathname");

    if (landingPageParam) {
        localStorage.setItem("landing_page", landingPageParam.toLowerCase());
    }

    useEffect(() => {
        const getUserOrgs = async () => {
            await dispatch(fetchUserOrgsThunk());
        };

        if (!userOrgs.data.length && userOrgs.status === "pristine") {
            getUserOrgs();
        }

        if (hasSession && userOrgs.status === "done") {
            const landingPageValue = localStorage.getItem("landing_page")?.toLowerCase();

            const handleUpdateSelectedOrg = async (orgId: number) => {
                setAvoidMultipleGetSession(true);
                const { data } = await updateUserOrg(orgId);

                if (data.Info === "OK") {
                    await dispatch(fetchSession());
                }
            };

            const updateLandingPage = async (value: string) => {
                setRequestStatus("fetching");
                try {
                    await updateLandingPageService(value);
                    setRequestStatus("done");
                } catch (err) {
                    console.error(err);
                    setRequestStatus("error");
                } finally {
                    localStorage.removeItem("landing_page");
                }
            };

            if (sessionData !== null && userOrgs.data.length && sessionData.authn.user.org_selected.id === null) {
                if (defaultOrg || lastPathname?.includes("default_org")) {
                    if (avoidMultipleGetSession) return;
                    if (defaultOrg) {
                        const orgToNumber = parseInt(defaultOrg);
                        handleUpdateSelectedOrg(orgToNumber);
                        return;
                    }

                    if (lastPathname) {
                        const { default_org } = qs.parse(lastPathname.split("?")[1], { ignoreQueryPrefix: true });

                        if (default_org && typeof default_org === "string") {
                            const orgToNumber = parseInt(default_org);

                            if (isNaN(orgToNumber)) {
                                console.error("Invalid default_org");
                                return;
                            }
                            handleUpdateSelectedOrg(orgToNumber);
                            return;
                        }
                    }
                } else {
                    if (orgUpdated) return;
                    const handleUpdateSelectedOrg = async (orgId: number) => {
                        setOrgUpdated(true);
                        const { data } = await updateUserOrg(orgId);

                        if (data.Info === "OK") {
                            try {
                                await dispatch(fetchSession());
                            } catch (err) {
                                console.error(err);
                            }
                        }
                    };

                    const handleSetDefaultOrg = async (org: string) => {
                        const orgToNumber = parseInt(org);

                        if (isNaN(orgToNumber)) {
                            console.error("Invalid default_org");
                            return;
                        }

                        await handleUpdateSelectedOrg(orgToNumber);
                    };

                    const orgsCount = userOrgs.data.length;
                    const allOrgsAreProfile = Boolean(
                        orgsCount && userOrgs.data.every((org) => org.user.function_name === "PROFILE")
                    );
                    const orgsUserIsNotProfile = userOrgs.data.filter((org) => org.user.function_name !== "PROFILE");

                    // handleSetDefaultOrg should execute the useEffect in Home.tsx redirecting the user to the corresponding page

                    if (orgsCount === 0) {
                        navigate(urlPaths.Home);
                    }

                    // If default org is present in URL
                    if (defaultOrg) {
                        handleSetDefaultOrg(defaultOrg);
                        return;
                    }

                    // Profile only in all hubs OR has only on hub
                    if (allOrgsAreProfile || orgsCount === 1) {
                        const orgId = userOrgs.data[0].id;
                        handleUpdateSelectedOrg(orgId);
                        return;
                    }

                    // Redirect to Select hub page
                    if (orgsCount > 1 && orgsUserIsNotProfile.length) {
                        navigate(urlPaths.ChooseYourHub);
                    }
                }
                return;
            }

            if (requestStatus === "pristine" && (landingPageValue === "ld" || landingPageValue === "pace")) {
                updateLandingPage(landingPageValue);
                landingPageValue === "ld" && navigate(landingPageValue === "ld" ? REDIRECT_LD : REDIRECT_PACE);
                return;
            }

            if (!lastPathname && sessionData && sessionData.authn.user.default_landing_page) {
                sessionData.authn.user.default_landing_page === "ld" && navigate(REDIRECT_LD);
                sessionData.authn.user.default_landing_page === "pace" && navigate(REDIRECT_PACE);
                return;
            }

            if (ability.can("see", "PACE") || ability.can("see", "PACE_ASSESSMENT")) {
                navigate(localStorage.getItem("lastPathname") || REDIRECT_PACE);
                return;
            }

            if (
                ability.can("see", "LD_FREE") ||
                ability.can("see", "LD_BASIC") ||
                ability.can("see", "LD_LIMITED") ||
                ability.can("see", "LD_TALENT")
            ) {
                navigate(localStorage.getItem("lastPathname") || REDIRECT_LD);
                return;
            }
        }
    }, [
        hasSession,
        navigate,
        ability,
        landingPageParam,
        requestStatus,
        lastPathname,
        sessionData,
        userOrgs,
        dispatch,
        defaultOrg,
        avoidMultipleGetSession,
        orgUpdated,
    ]);

    return fetched && error ? (
        error.code === 401 ? (
            <Loading />
        ) : (
            <Loading>
                <Typography color="text.primary" variant="h4">
                    {error.message}
                </Typography>
            </Loading>
        )
    ) : (
        <Loading />
    );
};

export default Home;
